import { useTranslation } from 'react-i18next';

import { snack } from '@innovamat/glimmer-components';
import { useCopyToClipboard } from '@innovamat/hooks';

import { useUser } from '../../../../../user-management';
import { useClassrooms } from '../../../../hooks/use-classrooms';
import { useSchoolStudents } from '../../providers/school-students-provider';

import type { SchoolStudent } from '../../types/student';

import {
  ActionsDropdown,
  type CreateItem,
} from '../../../../components/common-table-components/actions-dropdown';
import { useOrganization } from '../../../../hooks/use-organization';

type Props = {
  student: SchoolStudent;
};

function SchoolStudentsTableActionsCell({
  student,
}: Props): JSX.Element | null {
  const {
    onRecoverStudent,
    isDeletedStudentsPage,
    onChangeClassroom,
    onDeleteStudent,
  } = useSchoolStudents();
  const { t } = useTranslation();
  const { isGoogleClassroom } = useClassrooms({ mine: false });

  const [_, copy] = useCopyToClipboard();
  const { isRole } = useUser();
  const isFromGoogle = isGoogleClassroom(student.classroomId);

  const { isRosteringEnabled, isJourneysEnabled } = useOrganization();

  const recoverItem: CreateItem = {
    text: t('students.table.recover'),
    icon: 'JoinClassIcon',
    state: 'active',
    onSelectItem: () => onRecoverStudent(student),
  };

  const moveStudentItem: CreateItem = {
    text: t('students.button.moveClassroom'),
    icon: 'MoveIcon',
    state: isFromGoogle ? 'disabled' : 'active',
    onSelectItem: () => onChangeClassroom(student),
  };

  const deleteStudentItem: CreateItem = {
    text: t('students.button.removeStudent'),
    icon: 'DeleteIcon',
    state: isFromGoogle ? 'disabled' : 'active',
    onSelectItem: () => onDeleteStudent(student),
  };

  const copyIdItem: CreateItem | undefined = isRole.Advisor
    ? {
        text: t('manager.table.copyId'),
        icon: 'CopyIcon',
        state: 'active',
        onSelectItem: () => {
          copy(student?.id || '');
          snack.success(t('manager.table.copiedId'));
        },
      }
    : undefined;

  const getItems = () => {
    if (isRosteringEnabled) return [copyIdItem];
    if (isDeletedStudentsPage) return [recoverItem, copyIdItem];

    if (isJourneysEnabled) {
      return [];
    }

    return [moveStudentItem, deleteStudentItem, copyIdItem];
  };

  const items = getItems().filter((item) => item !== undefined) as CreateItem[];

  if (items.length === 0) {
    return null;
  }

  return <ActionsDropdown dropdownId="school-students-button" items={items} />;
}

export { SchoolStudentsTableActionsCell };
